/* global L */
// Polyfill needeed for Safari 13...
import EventTarget from '@ungap/event-target';
import postbox from '../../postbox.mjs';
import configuration from '../../configuration.mjs';
import { getPageLanguage } from '../../utils/get-page-language.mjs';
import markerIconSvg from './../../../assets/images/leaflet-marker-icon.svg?no-inline';
import markerIconShadowSvg from './../../../assets/images/leaflet-marker-shadow.svg?no-inline';

export default class SearchResultMapHandler extends EventTarget {
  /**
   * @type {HTMLElement}
   */
  #mapContainer;

  #map;
  #markersLayer;
  #handleNewResultsBound = null;
  #handleModeChangedBound = null;

  #markerIcon = null;

  #isInitialized = false;
  #isDestroyed = false;

  // For testing purposes
  get mapInstance() {
    return this.#map;
  }

  // For testing purposes
  get markersLayerInstance() {
    return this.#markersLayer;
  }

  constructor(containerElement) {
    super();

    this.#mapContainer = containerElement.querySelector('#map-container');
    this.#handleNewResultsBound = this.#handleNewResults.bind(this);
    this.#handleModeChangedBound = this.#handleModeChanged.bind(this);

    postbox.addEventListener('map-search-results-updated', this.#handleNewResultsBound);
    postbox.addEventListener('map-search-result-mode-changed', this.#handleModeChangedBound);
  }

  async #initMap() {
    if(this.#isInitialized) {
      return;
    }

    this.#isInitialized = true;

    await import('leaflet');
    await import('leaflet.markercluster');

    if(this.#isDestroyed) {
      return;
    }

    const mapElement = this.#mapContainer.querySelector('#map-results');
    this.#map = L.map(mapElement);

    // Leaflet does some weird stuff to "find" the default icons,
    // which of course breaks everything with Vite. So we need
    // to define our own icon to get it working properly.
    this.#markerIcon = L.icon({
      iconUrl: markerIconSvg,
      shadowUrl: markerIconShadowSvg,
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      tooltipAnchor: [16, -28],
      shadowSize: [41, 41]
    });

    const tileLayer = new L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    });

    this.#map.addLayer(tileLayer);
    this.#resetMapSizeAndPosition();
  }

  destroy() {
    postbox.removeEventListener('map-search-results-updated', this.#handleNewResultsBound);
    postbox.removeEventListener('map-search-result-mode-changed', this.#handleModeChanged);

    this.#map?.remove();
    this.#map = null;

    this.#isDestroyed = true;
  }

  #resetMapSizeAndPosition() {
    this.#map?.invalidateSize();
    this.#map?.fitBounds(this.#getBoundsForCurrentSite());
  }

  async #handleModeChanged(event) {
    const { mode } = event.detail;
    if(mode === 'map') {
      this.#mapContainer.classList.add('show');
      await this.#initMap();
      this.#resetMapSizeAndPosition();
    } else {
      this.#mapContainer.classList.remove('show');
    }
  }

  #getBoundsForCurrentSite() {
    switch (configuration.website) {
      case 'Sweden':
      case 'Commercial':
        return [[55.3617373725, 11.0273686052], [69.1062472602, 23.9033785336]];
      case 'International':
        return [[44.103556825977606, 4.724121093750001], [35.862559802076454, -10.56884765625]];
      default:
        return [[-90, -180], [90, 180]];
    }
  }

  async #handleNewResults(event) {
    if(!this.#isInitialized) {
      await this.#initMap();
    }

    const { results } = event.detail;
    this.#markersLayer?.clearLayers();

    if(!this.#markersLayer) {
      this.#markersLayer = L.markerClusterGroup({
        spiderfyOnMaxZoom: true,
        showCoverageOnHover: false,
        zoomToBoundsOnClick: true
      });

      this.#map.addLayer(this.#markersLayer);
    }

    this.#markersLayer.addLayers(results.filter(r => !!r.latitude && !!r.longitude && !!r.shortId).map(result => {
      const marker = L.marker([result.latitude, result.longitude], { icon: this.#markerIcon });

      const popup = L.popup({
        className: 'popup-estate',
        autoPan: false
      });

      const abortController = new AbortController();

      marker.on('popupopen', async () => {
        this.#showPopupWithLoader();

        const signal = abortController.signal;

        const culture = getPageLanguage();
        const url = `/api/${culture}/estate-search/result-item/${result.shortId}`;

        let response;
        try {
          response = await fetch(url, { signal });
        } catch(error) {
          if(error.name === 'AbortError') {
            return;
          }

          throw error;
        }

        if(!response.ok) {
          console.error(`Failed to fetch estate result item: ${response.status} ${response.statusText}`);
          this.#setPopupContent('<p>Failed to load content.</p>');
          return;
        }

        const content = await response.text();
        this.#setPopupContent(content);
      });

      marker.on('popupclose', () => this.#hidePopup());

      marker.bindPopup(popup);

      return marker;
    }));

    // Zoom map to fit all markers
    this.#map.fitBounds(this.#markersLayer.getBounds());
  }

  #showPopupWithLoader() {
    const popupElement = this.#mapContainer.querySelector('.map-popup');
    popupElement.classList.add('visible');

    const loader = popupElement.querySelector('.loader');
    loader.classList.add('visible');

    const contentElement = popupElement.querySelector('.content');
    contentElement.innerHTML = '';
  }

  #setPopupContent(content) {
    const popupElement = this.#mapContainer.querySelector('.map-popup');
    const loader = popupElement.querySelector('.loader');
    loader.classList.remove('visible');
    popupElement.querySelector('.content').innerHTML = content;
  }

  #hidePopup() {
    const popupElement = this.#mapContainer.querySelector('.map-popup');
    popupElement.classList.remove('visible');
  }
}
